/**
 * Phase 28 D1 — hybrid optimistic + invalidate mutation hooks for timesheet
 * approve / reject / resubmit. Centralises the cache-flip + refetch behaviour
 * that PendingTimesheetsPage, TimesheetDetailPage, and the cockpit cards
 * previously open-coded inconsistently. Also fixes MKS-109 (approved
 * timesheet didn't disappear) and MKS-112 (no auto-refresh).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  approveTimesheet,
  rejectTimesheet,
  submitTimesheet,
  type TimesheetDetailsResponse,
  type TimesheetResponse,
} from '../services/api/timeEntry';
import { invalidateMany } from './queryUtils';

interface OptimisticContext {
  previousDetail?: TimesheetDetailsResponse;
}

interface MutationOptions {
  onSuccessToast?: (data: TimesheetDetailsResponse | TimesheetResponse) => void;
  onErrorToast?: (message: string) => void;
}

interface ApproveVariables {
  id: number;
  entryIds?: number[];
  managerOnly?: boolean;
}

interface RejectVariables {
  id: number;
  reason: string;
  entryIds?: number[];
  managerOnly?: boolean;
}

interface SubmitVariables {
  id: number;
}

export const INVALIDATE_KEYS = [
  ['pendingTimesheets'],
  ['allTimesheets'],
  // Admin queue uses both list + aggregate keys (AdminPendingTimesheetsPage:244/259).
  ['adminTimesheets'],
  ['adminTimesheetsAggregate'],
  ['approvalCounts'],
  ['cockpit'],
  // Employee-side caches the user's own current timesheet + history; both must
  // refetch so the rejection banner appears (and disappears on resubmit).
  ['currentTimesheet'],
  ['timesheetHistory'],
  // Entry rows on TimeEntryPage (calendar grid + reported-entries table) carry
  // entry.state and rejectionReason — must refetch so the red ✗ / "Needs
  // Revision" indicators clear when the timesheet is approved or resubmitted.
  ['monthlyEntries'],
] as const;

function invalidateAfterTimesheetMutation(
  queryClient: ReturnType<typeof useQueryClient>,
  id: number
): void {
  invalidateMany(queryClient, [
    ['timesheet', id],
    // Audit trail is keyed per (subjectType, subjectId).
    ['auditTrail', 'Timesheet', id],
    ...INVALIDATE_KEYS,
  ]);
}

function flipState(
  queryClient: ReturnType<typeof useQueryClient>,
  id: number,
  nextState: 'approved' | 'rejected' | 'submitted'
): TimesheetDetailsResponse | undefined {
  const previous = queryClient.getQueryData<TimesheetDetailsResponse>(['timesheet', id]);
  if (previous) {
    const data = (previous as { data?: { state?: string } }).data;
    if (data) {
      queryClient.setQueryData(['timesheet', id], {
        ...previous,
        data: { ...data, state: nextState },
      });
    } else {
      // Bare-object cache shape (test fixtures use this) — flip top-level.
      queryClient.setQueryData(['timesheet', id], {
        ...(previous as unknown as Record<string, unknown>),
        state: nextState,
      });
    }
  }
  return previous;
}

export function useApproveTimesheetMutation(opts: MutationOptions = {}) {
  const queryClient = useQueryClient();

  return useMutation<TimesheetDetailsResponse, Error, ApproveVariables, OptimisticContext>({
    mutationFn: ({ id, entryIds, managerOnly }) =>
      approveTimesheet(id, entryIds, managerOnly) as unknown as Promise<TimesheetDetailsResponse>,

    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: ['timesheet', id] });
      const previousDetail = flipState(queryClient, id, 'approved');
      return { previousDetail };
    },

    onError: (err, { id }, context) => {
      if (context?.previousDetail !== undefined) {
        queryClient.setQueryData(['timesheet', id], context.previousDetail);
      }
      opts.onErrorToast?.(err.message || 'Schválení selhalo');
    },

    onSuccess: (data) => {
      opts.onSuccessToast?.(data);
    },

    onSettled: (_data, _err, { id }) => {
      invalidateAfterTimesheetMutation(queryClient, id);
    },
  });
}

export function useRejectTimesheetMutation(opts: MutationOptions = {}) {
  const queryClient = useQueryClient();

  return useMutation<TimesheetDetailsResponse, Error, RejectVariables, OptimisticContext>({
    mutationFn: ({ id, reason, entryIds, managerOnly }) =>
      rejectTimesheet(id, reason, entryIds, managerOnly) as unknown as Promise<TimesheetDetailsResponse>,

    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: ['timesheet', id] });
      const previousDetail = flipState(queryClient, id, 'rejected');
      return { previousDetail };
    },

    onError: (err, { id }, context) => {
      if (context?.previousDetail !== undefined) {
        queryClient.setQueryData(['timesheet', id], context.previousDetail);
      }
      opts.onErrorToast?.(err.message || 'Odmítnutí selhalo');
    },

    onSuccess: (data) => {
      opts.onSuccessToast?.(data);
    },

    onSettled: (_data, _err, { id }) => {
      invalidateAfterTimesheetMutation(queryClient, id);
    },
  });
}

export function useSubmitTimesheetMutation(opts: MutationOptions = {}) {
  const queryClient = useQueryClient();

  return useMutation<TimesheetResponse, Error, SubmitVariables, OptimisticContext>({
    mutationFn: ({ id }) => submitTimesheet(id),

    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: ['timesheet', id] });
      const previousDetail = flipState(queryClient, id, 'submitted');
      return { previousDetail };
    },

    onError: (err, { id }, context) => {
      if (context?.previousDetail !== undefined) {
        queryClient.setQueryData(['timesheet', id], context.previousDetail);
      }
      opts.onErrorToast?.(err.message || 'Odeslání selhalo');
    },

    onSuccess: (data) => {
      opts.onSuccessToast?.(data);
    },

    onSettled: (_data, _err, { id }) => {
      invalidateAfterTimesheetMutation(queryClient, id);
    },
  });
}
